"use client";

import type { CountryCode } from "libphonenumber-js";
import { useEffect, useState } from "react";
import { CaretSortIcon, CheckIcon } from "@radix-ui/react-icons";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import ReactCountryFlag from "react-country-flag";

import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@fluid-app/ui";

// Define the country option interface
export interface CountryOption {
  code: CountryCode;
  name: string;
  dialCode: string;
}

// Get country name from country code
const getCountryName = (countryCode: string) => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(countryCode);
  } catch {
    return countryCode;
  }
};

// Get all countries supported by libphonenumber-js
const countries = getCountries();

// Format the country data for use in the combobox
const countryOptions: CountryOption[] = countries
  .map((country) => ({
    code: country,
    name: getCountryName(country) ?? country,
    dialCode: `+${getCountryCallingCode(country)}`,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

export interface CountrySelectorProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (country: CountryOption) => void;
}

export function CountrySelector({
  value,
  onChange,
  onSelect,
}: CountrySelectorProps) {
  const [open, setOpen] = useState(false);

  // Initialize with a default country to avoid undefined values
  const getInitialCountry = (): CountryOption => {
    const found = countryOptions.find((option) => option.code === value);
    const usCountry = countryOptions.find((option) => option.code === "US");
    return (
      found ??
      usCountry ??
      countryOptions[0] ?? {
        code: "US",
        name: "United States",
        dialCode: "+1",
      }
    );
  };

  const [selectedCountry, setSelectedCountry] =
    useState<CountryOption>(getInitialCountry());

  // Update selected country when value changes
  useEffect(() => {
    const country = countryOptions.find((option) => option.code === value);
    if (country) {
      setSelectedCountry(country);
    }
  }, [value]);

  const handleSelect = (country: CountryOption) => {
    setSelectedCountry(country);
    onChange(country.code);
    setOpen(false);

    if (onSelect) {
      onSelect(country);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="flex h-9 w-full items-center justify-between border-input px-3"
        >
          <div className="flex items-center gap-2">
            <ReactCountryFlag
              countryCode={selectedCountry.code}
              svg
              style={{
                width: "1.2em",
                height: "1.2em",
              }}
              aria-label={selectedCountry.name}
            />
            <span className="whitespace-nowrap text-xs opacity-50">
              {selectedCountry.dialCode}
            </span>
          </div>
          <CaretSortIcon className="ml-4 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0">
        <Command>
          <CommandInput placeholder="Search country..." />
          <CommandEmpty>No country found.</CommandEmpty>
          <CommandList>
            <CommandGroup>
              {countryOptions.map((country) => (
                <CommandItem
                  key={country.code}
                  value={`${country.name} ${country.dialCode} ${country.code}`}
                  onSelect={() => handleSelect(country)}
                  className="flex items-center gap-2"
                >
                  <ReactCountryFlag
                    countryCode={country.code}
                    svg
                    style={{
                      width: "1.2em",
                      height: "1.2em",
                    }}
                    aria-label={country.name}
                  />
                  <span>{country.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {country.dialCode}
                  </span>
                  {country.code === selectedCountry.code && (
                    <CheckIcon className="h-4 w-4" />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
